/**
 * Made.bb Login Page
 * Uses shared Firebase authentication
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export const MadebbLogin: React.FC = () => {
  const navigate = useNavigate();
  const { signInWithGoogle, signInWithEmail, error: authError } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle('madebb');
      navigate('/madebb/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await signInWithEmail(email, password, 'madebb');
      navigate('/madebb/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            padding: 4,
            borderRadius: 4,
            background: 'white'
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 3
            }}
          >
            <img
              src="/madebb/logo.png"
              alt="Made.bb"
              style={{
                maxWidth: '300px',
                width: '100%',
                height: 'auto'
              }}
            />
          </Box>

          <Typography
            variant="h5"
            component="h1"
            sx={{
              textAlign: 'center',
              marginBottom: 3,
              fontWeight: 600,
              color: '#2c3e50'
            }}
          >
            Staff Login
          </Typography>

          {(error || authError) && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {error || authError}
            </Alert>
          )}

          {/* Email/Password Form */}
          <form onSubmit={handleEmailSignIn}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              inputProps={{ 'data-testid': 'login-email' }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              inputProps={{ 'data-testid': 'login-password' }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                marginTop: 3,
                marginBottom: 2,
                padding: '12px',
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5568d3, #653a8b)'
                }
              }}
              data-testid="login-submit"
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>

          <Divider sx={{ marginY: 2 }}>OR</Divider>

          {/* Google Sign In */}
          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{
              padding: '12px',
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#5568d3',
                background: 'rgba(102, 126, 234, 0.04)'
              }
            }}
            data-testid="login-google"
          >
            Sign in with Google
          </Button>

          {/* Back to Landing */}
          <Box sx={{ textAlign: 'center', marginTop: 3 }}>
            <Button
              variant="text"
              onClick={() => navigate('/madebb')}
              sx={{ color: '#666' }}
            >
              Back to Landing Page
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
